import { useState, type FormEvent } from 'react';
import { cnpjValido } from '@raiox/domain';
import { trpc } from '../lib/trpc.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { competencia as fmtCompetencia, data as fmtData, inteiro, moeda } from '../lib/formato.js';

export function Empresas({ papel }: { papel: string }) {
  const admin = papel === 'admin';
  const utils = trpc.useUtils();

  const lista = trpc.empresas.listar.useQuery();
  const sugestoes = trpc.empresas.sugestoes.useQuery();
  const pendentes = trpc.empresas.naoClassificados.useQuery({ limite: 100 });

  const [cnpj, setCnpj] = useState('');
  const [razao, setRazao] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const criar = trpc.empresas.criar.useMutation({
    onSuccess: (r) => {
      setCnpj('');
      setRazao('');
      setAviso(
        r.reprocessados > 0
          ? `Empresa cadastrada. ${inteiro(r.reprocessados)} arquivo(s) voltaram para a fila — as notas aparecem assim que o processamento terminar.`
          : 'Empresa cadastrada. Nenhum arquivo pendente envolvia este CNPJ.',
      );
      // Carteira nova muda o roteamento e, por tabela, tudo que já está em tela.
      void utils.invalidate();
    },
  });

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    setAviso(null);
    criar.mutate({ cnpj, razaoSocial: razao });
  };

  const digitos = cnpj.replace(/\D/g, '');
  const cnpjOk = digitos.length === 14 && cnpjValido(digitos);
  const podeEnviar = admin && cnpjOk && razao.trim() !== '' && !criar.isPending;

  return (
    <>
      <section className="panel">
        <h2 className="eyebrow">Carteira</h2>
        {lista.isPending ? (
          <Carregando linhas={4} rotulo="Carregando as empresas" />
        ) : lista.error ? (
          <Erro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} />
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>CNPJ</th>
                  <th>Razão social</th>
                  <th>UF</th>
                  <th className="num">Documentos</th>
                  <th className="num">Emitidas</th>
                  <th className="num">Tomadas</th>
                  <th className="num">Valor</th>
                </tr>
              </thead>
              <tbody>
                {(lista.data ?? []).map((e) => (
                  <tr key={e.id}>
                    <td className="mono nowrap">{e.cnpjFormatado}</td>
                    <td className="strong">{e.razaoSocial}</td>
                    <td>{e.uf ?? '—'}</td>
                    <td className="num">{inteiro(e.documentos)}</td>
                    <td className="num">{inteiro(e.emitidas)}</td>
                    <td className="num">{inteiro(e.tomadas)}</td>
                    <td className="num">{moeda(e.valorTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="foot">
          A nota entra na empresa cujo CNPJ aparece como emitente (emitida) ou como destinatário
          (tomada). Quando o escritório atende os dois lados, a mesma nota aparece nas duas — é
          receita de uma e custo da outra.
        </p>
      </section>

      <section className="panel">
        <h2 className="eyebrow">Cadastrar empresa</h2>
        {!admin ? (
          <p className="muted">Só o perfil administrador cadastra empresa.</p>
        ) : (
          <form className="form-linha" onSubmit={enviar}>
            <label>
              <span>CNPJ</span>
              <input
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                placeholder="00.000.000/0001-00"
                inputMode="text"
                maxLength={18}
                aria-invalid={digitos.length === 14 && !cnpjOk}
              />
            </label>
            <label className="cresce">
              <span>Razão social</span>
              <input
                value={razao}
                onChange={(e) => setRazao(e.target.value)}
                placeholder="Nome da empresa no contrato social"
                maxLength={255}
              />
            </label>
            <button type="submit" className="btn" disabled={!podeEnviar}>
              {criar.isPending ? 'Cadastrando…' : 'Cadastrar'}
            </button>
          </form>
        )}
        {digitos.length === 14 && !cnpjOk ? (
          <p className="alert crit">Dígito verificador não confere — confira o CNPJ digitado.</p>
        ) : null}
        {criar.error ? <p className="alert crit">{criar.error.message}</p> : null}
        {aviso ? <p className="alert ok">{aviso}</p> : null}
      </section>

      <section className="panel">
        <h2 className="eyebrow">
          Sugestões de cadastro
          {sugestoes.data && sugestoes.data.length > 0 ? (
            <span className="count">{inteiro(sugestoes.data.length)}</span>
          ) : null}
        </h2>
        {sugestoes.isPending ? (
          <Carregando linhas={3} rotulo="Procurando CNPJs sem cadastro" />
        ) : (sugestoes.data ?? []).length === 0 ? (
          <Vazio titulo="Nenhum CNPJ pendente">
            Todo XML enviado encontrou uma empresa da carteira.
          </Vazio>
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>CNPJ</th>
                  <th>Nome no XML</th>
                  <th className="num">Notas</th>
                  <th className="num">Valor</th>
                  <th>Aparece como</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(sugestoes.data ?? []).map((s) => (
                  <tr key={s.cnpj}>
                    <td className="mono nowrap">{s.cnpjFormatado}</td>
                    <td className="trunc">{s.nome || '—'}</td>
                    <td className="num">{inteiro(s.documentos)}</td>
                    <td className="num">{moeda(s.valorTotal)}</td>
                    <td className="muted">
                      {s.comoEmitente === s.documentos
                        ? 'sempre emitente'
                        : s.comoEmitente === 0
                          ? 'sempre destinatário'
                          : `${inteiro(s.comoEmitente)} como emitente`}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn sm"
                        disabled={!admin || criar.isPending}
                        title={admin ? 'Cadastrar e reprocessar as notas' : 'Requer perfil administrador'}
                        onClick={() => {
                          setAviso(null);
                          criar.mutate({ cnpj: s.cnpj, razaoSocial: s.nome || s.cnpjFormatado });
                        }}
                      >
                        Cadastrar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="foot">
          Estes CNPJs apareceram nos XMLs mas não são de nenhuma empresa da carteira. Cadastrar já
          reprocessa os arquivos guardados — não é preciso pedir os XMLs de novo ao cliente.
        </p>
      </section>

      <section className="panel">
        <h2 className="eyebrow">
          Não classificados
          {pendentes.data && pendentes.data.total > 0 ? (
            <span className="count crit">{inteiro(pendentes.data.total)}</span>
          ) : null}
        </h2>
        {pendentes.isPending ? (
          <Carregando linhas={3} rotulo="Carregando pendências" />
        ) : (pendentes.data?.total ?? 0) === 0 ? (
          <Vazio titulo="Nada pendente">
            Toda nota enviada foi atribuída a uma empresa da carteira.
          </Vazio>
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Número</th>
                  <th>Emissão</th>
                  <th>Competência</th>
                  <th>Emitente</th>
                  <th>Destinatário</th>
                  <th className="num">Valor</th>
                </tr>
              </thead>
              <tbody>
                {(pendentes.data?.itens ?? []).map((p) => (
                  <tr key={p.id}>
                    <td className="nowrap">{p.tipo}</td>
                    <td className="mono">{p.numero}</td>
                    <td className="nowrap">{fmtData(p.dataEmissao)}</td>
                    <td className="nowrap">{fmtCompetencia(p.competencia)}</td>
                    <td className="trunc">
                      <span className="mono">{p.emitenteDoc}</span>
                      <br />
                      <span className="muted">{p.emitenteNome}</span>
                    </td>
                    <td className="trunc">
                      <span className="mono">{p.destinatarioDoc}</span>
                      <br />
                      <span className="muted">{p.destinatarioNome}</span>
                    </td>
                    <td className="num">{moeda(p.valorTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="foot">
          Estas notas não entraram em nenhuma apuração: nem o emitente nem o destinatário é empresa
          cadastrada. Sem dono, elas somariam no fechamento de quem não é parte da operação.
        </p>
      </section>
    </>
  );
}
